import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types';

type SectionNavigation = Pick<
  NativeStackNavigationProp<RootStackParamList>,
  'getState' | 'popTo' | 'navigate'
>;

/**
 * Go to a top-level section (the navbar links, the mobile tabs).
 *
 * React Navigation 7's `navigate` pushes a fresh copy of a screen that is
 * already in the stack, so Home → Browse → Home mounted a second Home: it
 * reloaded its data and its band appeared a frame late. When the section is
 * already in the stack we pop back to it instead (keeping its state and
 * scroll position); otherwise it is pushed as before.
 */
export function goToSection<Name extends keyof RootStackParamList>(
  navigation: SectionNavigation,
  name: Name,
  params?: RootStackParamList[Name]
): void {
  const inStack = navigation.getState()?.routes.some((route) => route.name === name);
  // Cast: React Navigation's overloads don't accept a generic (name, params) pair.
  const nav = navigation as unknown as {
    popTo: (name: string, params?: object) => void;
    navigate: (name: string, params?: object) => void;
  };
  if (inStack) {
    nav.popTo(name, params);
  } else {
    nav.navigate(name, params);
  }
}
